import { useCallback, useContext, useMemo } from 'react';
import { IssuerPortfolioAssetFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAssetFullInfoModal.tsx';
import { useIssuerPortfolioTreemapChartByIdQuery } from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  ChartEmptyState,
  ChartTreemap,
  InfoCard,
  ModalProviderContext,
  Spinner,
  TreemapItem,
} from 'ui';

export interface ChartTreemapAssetProps {
  portfolioId: number;
}

export const ChartTreemapAsset = ({ portfolioId }: ChartTreemapAssetProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const {
    isFetching,
    isLoading,
    isError,
    data: dataTreemapChart,
  } = useIssuerPortfolioTreemapChartByIdQuery({
    portfolio_id: portfolioId,
  });

  const handleOnClick = useCallback(
    (assetId: number, assetName: string) => {
      openModal(IssuerPortfolioAssetFullInfoModal, {
        id: assetId,
        name: assetName,
        portfolioId,
      });
    },
    [openModal, portfolioId],
  );

  const [isHave, items] = useMemo<[boolean, TreemapItem[]]>(() => {
    const items = (dataTreemapChart?.data || [])
      .filter(item => item.value > 0)
      .map<TreemapItem>(item => {
        const isAsset = item.kind === 'asset';
        const showPnl = isAsset || item.native_currency !== item.currency;
        const name = isAsset
          ? item.name
          : [
              item.kind === 'bank_cash' ? 'Bank' : 'Broker',
              item.source_name,
              item.native_currency.toUpperCase(),
            ]
              .filter(Boolean)
              .join(' · ');

        return {
          name,
          pnl_percent: showPnl && item.pnl_ratio != null ? +item.pnl_ratio : undefined,
          value: +item.value,
          originalValue: +item.value,
          currency: item.currency,
          quantity: isAsset && item.quantity != null ? +item.quantity : undefined,
          pnl: showPnl && item.pnl != null ? +item.pnl : undefined,
          id: isAsset ? item.asset_id : undefined,
          onClick: isAsset ? handleOnClick : undefined,
        };
      });

    return [items.length > 0, items];
  }, [dataTreemapChart?.data, handleOnClick]);

  return (
    <InfoCard
      title="Assets"
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && !isLoading && <Spinner size="sm" />}
        </div>
      }
      classes={{
        content: 'p-0 px-6',
      }}
    >
      {!isLoading && isHave && <ChartTreemap items={items} colorNeutral="#24699F" />}
      {!isHave && (
        <div className="py-4 flex justify-center items-center font-medium text-lg">
          <ChartEmptyState isError={isError} isLoading={isLoading} />
        </div>
      )}
    </InfoCard>
  );
};
